import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * El pla d'acció a pantalla sencera, per projectar-lo.
 *
 * **Aquí sí que hi ha pantalles, i cadascuna és un termini.** És la diferència
 * amb la línia de temps, que va sencera i seguida perquè és una cadena: el pla,
 * en canvi, es llegeix per blocs —què fem ara, què dins d'un any, què cap a
 * l'horitzó— i cada bloc és una conversa diferent amb qui escolta. Passar de
 * l'un a l'altre és el ritme natural de qui ho explica.
 *
 * La primera pantalla és el futur preferit. Un pla d'acció sense el seu destí
 * al davant és una llista de feines, i el que s'ha de veure és per què cada
 * cosa hi és.
 *
 * La resta surt de projectar-ho en una aula: cos de text gran, una columna
 * estreta, tinta sobre paper i un sol accent. Qui ho fa i per quan van a sota
 * de cada acció, en petit: es consulten, no es proclamen.
 */

const HORITZONS = ['now', 'short', 'medium'];

const ActionPlanPresentation = ({ case_, exercise, actions, milestones, onClose }) => {
  const { t } = useTranslation();

  const perFita = {};
  milestones.forEach(m => { perFita[m.id] = m; });

  // Les pantalles: el destí, i després un termini per pantalla. Els terminis
  // buits no hi surten —una pantalla que digui «res a fer aquí» no aporta res.
  const pantalles = [];
  if ((exercise.vision_statement || '').trim()) {
    pantalles.push({ tipus: 'visio' });
  }
  HORITZONS.forEach(h => {
    const files = actions
      .filter(a => a.horizon === h)
      .sort((a, b) => (a.position - b.position) || (a.id - b.id));
    if (files.length) pantalles.push({ tipus: 'horitzo', horitzo: h, accions: files });
  });

  const [i, setI] = useState(0);
  const total = pantalles.length;

  const ves = useCallback((n) => {
    setI(prev => Math.max(0, Math.min(total - 1, n)));
  }, [total]);

  useEffect(() => {
    const tecla = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setI(prev => Math.min(prev + 1, total - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setI(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [total, onClose]);

  if (!total) return null;
  const pantalla = pantalles[i];
  const horitzo = exercise.horizon_label || t('backcasting.theHorizon');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-neutral-50">
      <div className="mx-auto max-w-5xl px-8 pb-32 pt-10 sm:px-12">

        <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-neutral-200 pb-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
            {case_?.name}
            <span className="text-neutral-400"> · {exercise.name}</span>
          </div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
            {t('backcasting.actionPlan')} · {horitzo}
          </div>
        </div>

        {pantalla.tipus === 'visio' ? (
          <div className="mt-16">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-700">
              {horitzo} · {t('backcasting.preferredFuture')}
            </p>
            <p className="mt-6 max-w-[62ch] text-[clamp(1.4rem,2.7vw,2.2rem)] font-medium leading-[1.35] tracking-[-0.015em] text-neutral-900">
              {exercise.vision_statement}
            </p>
            <p className="mt-12 max-w-[62ch] border-t border-neutral-200 pt-6 text-[clamp(1rem,1.4vw,1.25rem)] text-neutral-500">
              {t('backcasting.planIntro')}
            </p>
          </div>
        ) : (
          <div className="mt-14">
            <h2 className="text-[clamp(1.6rem,3.2vw,2.6rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-neutral-900">
              {t(`backcasting.horizonGroup.${pantalla.horitzo}`)}
            </h2>

            <ol className="mt-12 space-y-10">
              {pantalla.accions.map((accio, n) => (
                <li key={accio.id} className="relative pl-14">
                  <span className="absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full border border-accent-700 font-mono text-[12px] font-bold text-accent-700">
                    {n + 1}
                  </span>
                  <p className="max-w-[58ch] text-[clamp(1.15rem,1.9vw,1.6rem)] font-medium leading-[1.4] text-neutral-900">
                    {accio.description || t('backcasting.untitledAction')}
                  </p>

                  {/* Qui ho fa, per quan i d'on ve: es consulten, no es
                      proclamen, o sigui que van petits i en una sola línia. */}
                  <p className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-1 text-[clamp(0.85rem,1.1vw,1rem)] text-neutral-500">
                    {accio.owner && (
                      <span>
                        <span className="font-mono text-[0.75em] uppercase tracking-[0.14em] text-neutral-400">
                          {t('backcasting.owner')}
                        </span>{' '}
                        <span className="text-neutral-700">{accio.owner}</span>
                      </span>
                    )}
                    {accio.due_label && (
                      <span>
                        <span className="font-mono text-[0.75em] uppercase tracking-[0.14em] text-neutral-400">
                          {t('backcasting.due')}
                        </span>{' '}
                        <span className="text-neutral-700">{accio.due_label}</span>
                      </span>
                    )}
                    {accio.milestone_id && perFita[accio.milestone_id] && (
                      <span>
                        <span className="font-mono text-[0.75em] uppercase tracking-[0.14em] text-neutral-400">
                          {t('backcasting.fromMilestone')}
                        </span>{' '}
                        <span className="text-neutral-700">
                          {perFita[accio.milestone_id].title || t('backcasting.untitledMilestone')}
                          {perFita[accio.milestone_id].period_label
                            ? ` · ${perFita[accio.milestone_id].period_label}`
                            : ''}
                        </span>
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t border-neutral-200 bg-neutral-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-8 py-3 sm:px-12">
          <span className="hidden text-[11px] uppercase tracking-[0.12em] text-neutral-400 sm:block">
            ← → {t('backcasting.navigate')} · ESC {t('backcasting.close')}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => ves(i - 1)}
              disabled={i === 0}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-white disabled:opacity-40"
            >
              ←
            </button>
            <span className="min-w-[3.5rem] text-center font-mono text-sm text-neutral-500">
              {i + 1} / {total}
            </span>
            <button
              onClick={() => ves(i + 1)}
              disabled={i === total - 1}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-white disabled:opacity-40"
            >
              →
            </button>
            <button
              onClick={onClose}
              className="ml-3 rounded-md bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-neutral-700"
            >
              {t('backcasting.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPlanPresentation;
